import React, { lazy, Suspense } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Log from './log';
import t from './translate';

/**
 * Generate a random uuid.
 * @return {string}
 */
export function randUuid() {
    // our cor wants the UUIDS win upper case
    if (typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID().toUpperCase();
    }

    Log.notice('crypto.randomUUID', 'is not a function will use a fallback');
    return '10000000-1000-4000-8000-100000000000'
        .replace(/[018]/g, (c) => (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16))
        .toUpperCase();
}

/**
 * Sort an array of elements by the given property.
 * @param {object[]} arr
 * @param {string} propertyName
 * @param {boolean} reverse
 * @return {object[]} Sorted array.
 */
export function sortByProperty(arr, propertyName, reverse = false) {
    // sanity
    if (typeof arr !== 'object' || arr.length <= 1 || ['string', 'number'].indexOf(typeof arr[0][propertyName]) < 0) {
        return arr;
    }

    const applySorting = (objA, objB) => {
        const valA = String(objA[propertyName]).toUpperCase();
        const valB = String(objB[propertyName]).toUpperCase();

        let result = 0;

        if (valA < valB) {
            result = -1;
        } else if (valA > valB) {
            result = 1;
        } else {
            result = 0;
        }

        if (reverse) {
            result *= -1;
        }

        return result;
    };

    arr.sort(applySorting);

    return arr;
}

export function sortAsNumbers(items) {
    items.sort((a, b) => {
        a = parseInt(a, 10);
        b = parseInt(b, 10);

        if (a < b) {
            return -1;
        }
        if (a > b) {
            return 1;
        }
        // a must be equal to b
        return 0;
    });

    return items;
}

/**
 * Put the first letter in lower case.
 * @param str
 */
export function lcFirst(str) {
    return str.charAt(0).toLowerCase() + str.slice(1);
}

/**
 * Put the first letter in upper case.
 * @param str
 */
export function ucFirst(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Import on the fly a component.
 * @param {LazyImportOptions} options
 */
export const lazyImportComponent = (options) => {
    // sanity
    if (typeof options.componentPath === 'undefined' || options.componentPath.length === 0) {
        return '';
    }

    const ComponentToRender = lazy(() => import(`../modules/${options.componentPath}`));

    const componentProperties = typeof options.componentProperties !== 'undefined' ? options.componentProperties : {};

    return (
        <Suspense fallback={<CircularProgress />}>
            <ComponentToRender {...componentProperties} />
        </Suspense>
    );
};

/**
 * This code construction allows lazy loading of a component which is then served as memoized value.
 * As React does say, use this with caution, only if is really necessary.
 * @param {LazyImportOptions} options
 * @return {string|*}
 * @constructor
 */
function MemoizedLazyComponentLoader({ options }) {
    return lazyImportComponent(options);
}
export const MemoizedLazyComponent = React.memo(MemoizedLazyComponentLoader);

/**
 * Load a JSON from the given url.
 * @param jsonUrl
 * @return Promise<object>
 */
export const fetchJson = (jsonUrl) =>
    new Promise((resolve, reject) => {
        // thank you https://developer.mozilla.org/
        let fetchStatus;
        const startTime = new Date().getTime();
        fetch(jsonUrl, {
            method: 'GET',
            headers: {
                'Content-type': 'application/json;charset=UTF-8',
            },
        })
            .then((response) => {
                // Save the response status in a variable to use later.
                fetchStatus = response.status;
                // Handle success
                // eg. Convert the response to JSON and return
                return response.json();
            })
            .then((json) => {
                // Check if the response were success
                if (fetchStatus === 200) {
                    // Use the converted JSON
                    resolve(json);
                } else {
                    Log.error('FETCH', `Bad status received for JSON url ${jsonUrl}: ${fetchStatus}`);
                    reject();
                }
            })
            .catch((error) => {
                // Catch errors
                Log.error('FETCH', `There was an error while loading the JSON from url: ${jsonUrl}`);
                console.log(error);

                reject();
            })
            .finally(() => {
                const totalTime = new Date().getTime() - startTime;

                Log.debug('FETCH', `Fetched json from "${jsonUrl}" in ${totalTime}ms`);
            });
    });

/**
 * Make a request to the nodejs server (the one which handles the socket) and obtain a response.
 * The requests will use same endpoint used by the WCB to send real time notifications to nodejs server.
 * @param {NodeJsServerRequestType} requestType
 * @param {Object} params An optional object with parameters.
 * @return Promise<object> The return is a promise which resolves with an object.
 */
export const nodeJsServerRequest = (requestType, params = {}) =>
    new Promise((resolve, reject) => {
        const nodeJsServerUrl = `https://${window.location.host}/ws-wcf-push`;
        const startTime = new Date().getTime();
        fetch(nodeJsServerUrl, {
            method: 'POST',
            body: JSON.stringify({
                originator: 'WCFUI',
                requestType,
                requestParams: params,
                token: wcfUiCfg.swapiToken,
            }),
            headers: {
                'Content-type': 'application/json;charset=UTF-8',
            },
        })
            .then((response) => {
                if (response.status !== 200) {
                    throw `Invalid response code ${response.status} received from nodejs server`;
                }

                return response.text();
            })
            .then((responseText) => {
                resolve(JSON.parse(responseText));
            })
            .catch((error) => {
                // Catch errors
                Log.error('NODEJS REQUEST', `There was an error while loading the JSON from url: ${nodeJsServerUrl}`);
                console.log(error);

                reject();
            })
            .finally(() => {
                const totalTime = new Date().getTime() - startTime;

                Log.debug('NODEJS REQUEST', `Requested "${requestType}" in ${totalTime}ms`);
            });
    });

/**
 * Check how much can scroll over a container.
 * @param {HTMLElement} container
 */
export const remainingScroll = (container) => container.scrollHeight - container.offsetHeight - container.scrollTop;

/**
 * @param {string} phoneNumber
 * @return {string|*}
 */
export const addPhoneNumberPlus = (phoneNumber) =>
    phoneNumber.trim().charAt(0) !== '+' ? `+${phoneNumber}` : phoneNumber;

/**
 * @param {string} phoneNumber
 * @return {string|*}
 */
export const removePhoneNumberPlus = (phoneNumber) =>
    phoneNumber.trim().charAt(0) === '+' ? phoneNumber.substring(1) : phoneNumber;

/**
 * Schedule a function to be executed in background (if browser supports that).
 * @param {Function} task
 */
export const execBackgroundTask = (task) => {
    if ('requestIdleCallback' in window) {
        requestIdleCallback(task, { timeout: 100 });
    } else {
        setTimeout(task);
    }
};

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Deep merge two objects.
 * @param target
 * @param ...sources
 */
export function mergeDeep(target, ...sources) {
    if (!sources.length) return target;
    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
        Object.keys(source).forEach((key) => {
            if (isObject(source[key])) {
                if (!target[key]) Object.assign(target, { [key]: {} });
                mergeDeep(target[key], source[key]);
            } else {
                Object.assign(target, { [key]: source[key] });
            }
        });
    }

    return mergeDeep(target, ...sources);
}

/**
 * Calculate the difference between two arrays.
 * @param {Array} arr1
 * @param {Array}arr2
 * @return {Array}
 */
export function arrayDiff(arr1, arr2) {
    return arr1.filter((x) => !arr2.includes(x)).concat(arr2.filter((x) => !arr1.includes(x)));
}

/**
 * Formats the given sizes(in bytes) to what is needed (Bytes, KB, MB, GB).
 * This function can be extended to modify to TB, etc., too.
 * @param bytes
 * @param {number} decimals - how many decimals the result needs to have. If is not set, the default is 2;
 * @param {string} labelPosition - the position of the unit label. The type is string and can accept only two values: "before" and "after";
 
 * @returns An object having following properties:
 *  - sizeWithLabel: The formatted value with unit label already formatted for the needed language.
 *  - size: the actual converted value
 * If the given size is 0 it will return 0 bytes (depending on the language).
 * e.g.: 10 MB, MB 10
 */
export function fileSizeToHumanFormat(bytes, decimals, labelPosition) {
    const unitLabels = {
        Bytes: t('js_all_storage_size_bytes'),
        KB: t('js_all_storage_size_kilobytes'),
        MB: t('js_all_storage_size_megabytes'),
        GB: t('js_all_storage_size_gigabytes'),
    };

    const sizes = Object.keys(unitLabels).map((key) => unitLabels[key]);

    const k = 1024;
    const dm = decimals || 2;
    const i = bytes > 0 ? Math.floor(Math.log(bytes) / Math.log(k)) : 0;

    const result = {
        sizeWithLabel: '',
        size: parseFloat((bytes / k ** i).toFixed(dm)),
    };

    if (labelPosition === 'after') {
        if (bytes === 0) {
            result.sizeWithLabel = `0 ${sizes[1]}`;
        } else {
            result.sizeWithLabel = `${result.size} ${sizes[i]}`;
        }
    } else if (labelPosition === 'before') {
        if (bytes === 0) {
            result.sizeWithLabel = `${sizes[1]} 0`;
        } else {
            result.sizeWithLabel = `${result.size} ${sizes[i]}`;
        }
    }

    return result;
}

/**
 * Calculate the offset having the number of the page and the number of recods from one page.
 * @param page
 * @param recordsOnPage
 * @return {number}
 */
export function offsetFromPage(page, recordsOnPage) {
    return (page - 1) * recordsOnPage;
}

export function isSet(val) {
    return typeof val !== 'undefined' && val !== null;
}
